/**
 * Crew name → slug, for the wizard's live URL preview.
 *
 * The SERVER decides the real slug: `public.next_free_crew_slug` (3019) resolves
 * collisions against live crews AND tombstones inside the same transaction that
 * inserts the crew, because probing from here would be a check-then-act race
 * with anyone naming a crew the same thing in the same second.
 *
 * So this is a PREVIEW, not the answer. It derives the same base the server
 * starts from, which is what makes `URL quarterdown.com/c/pizza-dogs` honest
 * while you type — but a second PIZZA DOGS gets `pizza-dogs-2` and only the
 * server knows that. Deliberately kept identical to
 * `supabase/functions/_shared/crew.ts#crewSlugBase`; an edge function cannot
 * import from the app, which is why there are two copies rather than one.
 */

export function crewSlugBase(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 32)
    .replace(/-$/, '');
}

/**
 * The cap on a PERSON's display name, in the input rather than in a refusal.
 *
 * It lives beside the slug because it is the same kind of fact: a name is
 * turned into an identity somewhere else, and the field has to respect the
 * shape that identity can take.
 *
 * 24 is `profiles.user_key`'s own CHECK (`^[a-z0-9]{2,24}$`). The key is the
 * name stripped to `[a-z0-9]` and SLICED to 24, so past that length the name
 * and the login stop corresponding: two people differing only after the 24th
 * usable character derive the same key, and the second silently becomes `…2`
 * — a login nobody told them about.
 *
 * Capping the input stops that arising rather than explaining it afterwards.
 * Names already longer keep working untouched; this is the field, not a
 * constraint (Marcus, 2026-08-20: "we should probably enforce a character
 * limit").
 */
export const DISPLAY_NAME_MAX = 24;

/**
 * How long an invitation stays open (Marcus, 2026-08-20).
 *
 * The SERVER decides this — `INVITE_TTL_HOURS` in
 * `supabase/functions/_shared/crew.ts` — and the value travels back on every
 * invite as `expiresAt`. This copy exists for `LocalRepository`, which has no
 * server to ask, and for copy that states the window before one has been
 * minted. Keep the two in step; nothing enforces it, because an edge function
 * cannot import from the app.
 */
export const INVITE_TTL_DAYS = 7;
